import {FormControl, FormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    AmsService,
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    MessageInputModel,
    MessageInterface,
    MessageModel,
    MessageRecipientInterface,
    MessagingDistributionChannelsEnum,
    MessagingRequestTypesEnum,
    MessagingStatusesEnum,
    SendableMessageTemplateInterface
} from '@spa/api';

import {
    MessageRequestBodySelector,
    MessageRequestChannelEmailSelector,
    MessageRequestChannelPullSelector,
    MessageRequestChannelPushSelector,
    MessageRequestCustomerSelector,
    MessageRequestImportanceLevelSelector,
    MessageRequestKeyCodeSelector,
    MessageRequestMessagingTemplateKeyCodeSelector,
    MessageRequestPushNotificationBodySelector,
    MessageRequestSubjectSelector,
    MessageRequestTypeSelector,
} from 'appAssets/selectors';
import {CheckboxControl, Helper} from 'appShared/classes';
import {MessageRequestErrorStatesEnum} from 'appEntries/panel/enums';
import {MessageRequestControls, MessageRequestControlsInterface} from 'appAssets/descriptors';
import {BaseValidationFrom} from 'appEntries/panel/forms/base-validation.form';
import {AutocompleteOptionInterface} from 'appShared/components';

export class MessageRequestForm extends BaseValidationFrom<MessageRequestControlsInterface> {
    public form: FormGroup;
    public controls: MessageRequestControlsInterface;
    public messageStatus = MessagingStatusesEnum.PendingApproval;
    public selectedRecipients: MessageRecipientInterface[] = [];
    public selectedKeyCode: string;

    constructor(
        private readonly amsService: AmsService,
        private readonly messageKeyCodes: SendableMessageTemplateInterface[],
        private readonly hasCreateWithTemplatePermission: boolean,
        private readonly hasCreateManualPermission: boolean,
        public messageRequestModel: MessageModel = null
    ) {
        super();
    }

    private prepareFormValue(): void {
        Object.keys(this.controls).forEach((key: string) => {
            const control = this.controls[key];

            if (this.messageRequestModel) {
                control.value = this.getControlValue(control.name);
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });
    }

    private getImportanceLevel(): number {
        return this.form.value[MessageRequestImportanceLevelSelector] === true ? 1 : 0;
    }

    private setDistributionChannel(control: CheckboxControl, channel: MessagingDistributionChannelsEnum) {
        if (control && this.messageRequestModel.distributionChannels.indexOf(channel) >= 0) {
            control.value = true;
            control.setChecked(true);
        }
        control.disabled = !control.value;
    }

    private getDistributionChannels(): MessagingDistributionChannelsEnum[] {
        const result: MessagingDistributionChannelsEnum[] = [];

        if (this.controls.channelEmail.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Email);
        }
        if (this.controls.channelPush.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Push);
        }
        if (this.controls.channelPull.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Pull);
        }

        return result;
    }

    private prepareInputModel(): MessageInputModel {
        if (this.form.value[MessageRequestTypeSelector] === MessagingRequestTypesEnum.Template) {
            return new MessageInputModel(
                this.messageStatus,
                this.selectedRecipients,
                this.getDistributionChannels(),
                null,
                null,
                this.getImportanceLevel(),
                this.selectedKeyCode,
                [],
                null,
            );
        }

        return new MessageInputModel(
            this.messageStatus,
            this.selectedRecipients,
            this.getDistributionChannels(),
            this.form.value[MessageRequestSubjectSelector],
            this.form.value[MessageRequestBodySelector],
            this.getImportanceLevel(),
            this.form.value[MessageRequestKeyCodeSelector],
            [],
            this.form.value[MessageRequestPushNotificationBodySelector],
        );
    }

    private update(): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = this.prepareInputModel();

        return this.amsService.updateMessage(this.messageRequestModel.id, inputModel)
            .pipe(
                map((response) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            MessageRequestErrorStatesEnum.UpdateMessageRequestFailed
                        );
                    }

                    const messageData = response.data as MessageInterface;
                    this.messageRequestModel = new MessageModel(messageData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private create(): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = this.prepareInputModel();

        return this.amsService.createMessage(inputModel)
            .pipe(
                map(response => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            MessageRequestErrorStatesEnum.CreateMessageRequestFailed
                        );
                    }

                    const messageData = response.data as MessageInterface;
                    this.messageRequestModel = new MessageModel(messageData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private static prepareOptions(data: SendableMessageTemplateInterface[])
        : AutocompleteOptionInterface[] {
        if (!data || data.length === 0) {
            return [];
        }

        return data.map((item: SendableMessageTemplateInterface) => {
            return {
                label: item.name,
                value: item.keyCode
            };
        });
    }

    getControlValue(name: string): string | number | boolean {
        switch (name) {
            case MessageRequestSubjectSelector:
                return this.messageRequestModel.subject;
            case MessageRequestBodySelector:
                return this.messageRequestModel.body;
            case MessageRequestKeyCodeSelector:
                return this.messageRequestModel.keyCode;
            case MessageRequestMessagingTemplateKeyCodeSelector:
                return this.messageRequestModel.keyCode;
            case MessageRequestCustomerSelector:
                return this.messageRequestModel.customerId;
            case MessageRequestPushNotificationBodySelector:
                return this.messageRequestModel.pushNotificationBody;
            case MessageRequestChannelEmailSelector:
                return (this.messageRequestModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Email) >= 0);
            case MessageRequestChannelPushSelector:
                return (this.messageRequestModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Push) >= 0);
            case MessageRequestChannelPullSelector:
                return (this.messageRequestModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Pull) >= 0);
            }

        return null;
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.controls = MessageRequestControls(this.hasCreateWithTemplatePermission, this.hasCreateManualPermission);

        if (this.messageRequestModel) {
            this.controls.messageType.value = this.messageRequestModel.status;

            this.setDistributionChannel(this.controls.channelEmail, MessagingDistributionChannelsEnum.Email);
            this.setDistributionChannel(this.controls.channelPush, MessagingDistributionChannelsEnum.Push);
            this.setDistributionChannel(this.controls.channelPull, MessagingDistributionChannelsEnum.Pull);

            this.controls.pushNotificationBody.value = this.messageRequestModel.pushNotificationBody;
            this.controls.importanceLevel.setChecked(this.messageRequestModel.importanceLevel>0); 
        }
       
        this.controls.messageKeyCode.options = MessageRequestForm.prepareOptions(this.messageKeyCodes);
        this.prepareFormValue();
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!this.messageRequestModel) {
            return this.create();
        }

        return this.update();
    }
}
